import React, { useState, useEffect, useCallback } from 'react';
import { reportsApi } from '../../services/api';
import { toTitleCase } from '../../utils/billing';

function todayStr() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// Printable guest register — the legally required log of every guest who
// stayed (name, address, ID proof, nationality, stay dates) that police can
// ask to inspect. Pulled from data already captured at check-in; this page
// only adds a date-range filtered, print-friendly view of it.
export default function GuestRegisterPage({ hotel }) {
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!hotel?.id) return;
    setLoading(true);
    try {
      const data = await reportsApi.register(hotel.id, { start: startDate, end: endDate, search });
      setRows(data || []);
    } catch (err) {
      console.error(err);
      setRows([]);
    }
    setLoading(false);
  }, [hotel?.id, startDate, endDate, search]);

  useEffect(() => { load(); }, [load]);

  const formatName = (r) => {
    const parts = [r.salutation, r.guest_name].map(s => (s || '').trim()).filter(Boolean);
    return parts.length ? parts.map(toTitleCase).join(' ') : 'Guest';
  };

  return (
    <div className="panel animate-in">
      <div className="panel-head no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <span className="panel-title">📋 Guest Register</span>
        <button className="btn btn-sm" style={{ background: 'var(--accent)', color: 'white' }} onClick={() => window.print()}>
          🖨️ Print
        </button>
      </div>

      <div className="no-print" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '12px 24px 0' }}>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>From</span>
        <input type="date" className="input" style={{ width: 145, padding: '4px 8px', fontSize: 13 }} value={startDate} onChange={e => setStartDate(e.target.value)} />
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>To</span>
        <input type="date" className="input" style={{ width: 145, padding: '4px 8px', fontSize: 13 }} value={endDate} onChange={e => setEndDate(e.target.value)} />
        <input
          className="input"
          style={{ width: 200, padding: '4px 8px', fontSize: 13 }}
          placeholder="Search guest name / room..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{rows.length} entr{rows.length === 1 ? 'y' : 'ies'}</span>
      </div>

      <div style={{ padding: 24 }}>
        <div className="print-only" style={{ display: 'none', marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{hotel?.name || 'Hotel'} — Guest Register</div>
          <div style={{ fontSize: 12 }}>{startDate} to {endDate}</div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '100px 0', color: 'var(--muted)' }}>
            <div className="spin" style={{ marginBottom: 16 }}>🌀</div>
            <div>Loading register...</div>
          </div>
        ) : rows.length === 0 ? (
          <div className="no-print" style={{ textAlign: 'center', padding: '100px 0', background: 'var(--s1)', borderRadius: 12, color: 'var(--muted)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
            <div style={{ fontWeight: 700 }}>No guests arrived in this date range.</div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Guest Name</th>
                  <th>Address</th>
                  <th>ID Proof</th>
                  <th>Nationality</th>
                  <th>Phone</th>
                  <th>Room</th>
                  <th>Check-In</th>
                  <th>Check-Out</th>
                  <th className="no-print">Signature</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={r.reservation_id}>
                    <td>{idx + 1}</td>
                    <td>{formatName(r)}</td>
                    <td>{r.address || '—'}</td>
                    <td>{r.id_type ? `${r.id_type}: ${r.id_number || '—'}` : '—'}</td>
                    <td>{r.is_foreign ? `Foreign (${r.nationality || '—'})` : (r.nationality || 'Indian')}</td>
                    <td>{r.phone || '—'}</td>
                    <td>{r.room || '—'}</td>
                    <td>{r.check_in ? String(r.check_in).split('T')[0] : '—'}{r.check_in_time ? ` ${r.check_in_time}` : ''}</td>
                    <td>{r.status === 'checked-out' && r.check_out ? String(r.check_out).split('T')[0] : '—'}{r.status === 'checked-out' && r.check_out_time ? ` ${r.check_out_time}` : ''}</td>
                    <td className="no-print" style={{ minWidth: 80 }}></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .panel { box-shadow: none !important; border: none !important; }
        }
      `}</style>
    </div>
  );
}
